import { z } from 'zod';
import { resultPeriod, type ResultPeriodKpi } from './results';

/**
 * Indicadores de sucesso do produto (T-205, RF-51, PRD §31; DEC-071): as regras de cada métrica, em funções puras
 * (a API junta os dados do banco e chama estas funções; os testes reproduzem os casos de borda).
 */

/** Janelas do painel: os últimos N dias até hoje. */
export const SUCCESS_WINDOWS = [30, 90, 365] as const;
export type SuccessWindow = (typeof SUCCESS_WINDOWS)[number];
export const successQuerySchema = z.object({
  days: z.coerce
    .number()
    .refine((v): v is SuccessWindow => (SUCCESS_WINDOWS as readonly number[]).includes(v), 'Janela inválida.')
    .default(30),
});

/** Desvios que pedem ação (DEC-071): os alertas de resultado ruim e o desvio informado. */
export const DEVIATION_ALERT_TYPES = [
  'KPI_BELOW_TARGET',
  'CONSECUTIVE_DROP',
  'OBJECTIVE_AT_RISK',
  'MANUAL_DEVIATION',
] as const;

const DAY_MS = 86_400_000;
const toTime = (iso: string) => Date.parse(`${iso}T00:00:00Z`);
const toIso = (t: number) => new Date(t).toISOString().slice(0, 10);
export const addIsoDays = (iso: string, days: number) => toIso(toTime(iso) + days * DAY_MS);

/** Primeiro dia da janela de N dias que termina hoje (hoje incluído). */
export const windowStart = (today: string, days: number) => addIsoDays(today, -(days - 1));

/** Parte de um total, em % (null quando não há o que medir). */
export const rate = (part: number, total: number) =>
  total === 0 ? null : Math.round((part / total) * 1000) / 10;

export type DuePeriod = { start: string; end: string; deadline: string };

/**
 * Períodos de um KPI cujo prazo de lançamento (fim do período + tolerância) caiu na janela [from, to) — o prazo que
 * vence hoje ainda pode ser cumprido, como no alerta de dado desatualizado, que só cobra no dia seguinte —, dentro da
 * vigência e do horizonte do projeto.
 */
export function kpiDuePeriods(
  kpi: ResultPeriodKpi & {
    validTo: string | null;
    /** Dia em que o KPI foi cadastrado: prazo vencido antes disso não tinha como ser cumprido. */
    createdOn?: string;
  },
  horizon: { start: number; end: number },
  graceDays: number,
  from: string,
  to: string,
): DuePeriod[] {
  const out: DuePeriod[] = [];
  let p = resultPeriod(kpi, addIsoDays(from, -graceDays));
  for (let guard = 0; guard < 2000 && p.start <= to; guard += 1) {
    const deadline = addIsoDays(p.end, graceDays);
    const inside =
      deadline >= from &&
      deadline < to &&
      (kpi.createdOn === undefined || deadline >= kpi.createdOn) &&
      p.end >= kpi.validFrom &&
      (kpi.validTo === null || p.start <= kpi.validTo) &&
      Number(p.end.slice(0, 4)) >= horizon.start &&
      Number(p.start.slice(0, 4)) <= horizon.end;
    if (inside) out.push({ ...p, deadline });
    p = resultPeriod(kpi, addIsoDays(p.end, 1));
  }
  return out;
}

export type LaunchIndex = { periodEnd: string; launchedOn: string }[];

/**
 * Lançamentos de um KPI prontos para consulta: o primeiro lançamento (data local) de cada fim de período, em ordem —
 * montado uma vez por KPI, para não percorrer todos os lançamentos a cada período.
 */
export function indexLaunches(results: { periodEnd: string; launchedOn: string }[]): LaunchIndex {
  const first = new Map<string, string>();
  for (const r of results) {
    const seen = first.get(r.periodEnd);
    if (seen === undefined || r.launchedOn < seen) first.set(r.periodEnd, r.launchedOn);
  }
  return [...first]
    .map(([periodEnd, launchedOn]) => ({ periodEnd, launchedOn }))
    .sort((a, b) => (a.periodEnd < b.periodEnd ? -1 : a.periodEnd > b.periodEnd ? 1 : 0));
}

/**
 * O período foi lançado no prazo? Vale o primeiro lançamento com valor de um resultado do período (data local do
 * lançamento até o prazo, inclusive). Busca binária no índice do KPI.
 */
export function launchedOnTime(period: DuePeriod, index: LaunchIndex): boolean {
  let lo = 0;
  let hi = index.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (index[mid]!.periodEnd < period.start) lo = mid + 1;
    else hi = mid;
  }
  let first: string | undefined;
  for (let i = lo; i < index.length && index[i]!.periodEnd <= period.end; i += 1) {
    if (first === undefined || index[i]!.launchedOn < first) first = index[i]!.launchedOn;
  }
  return first !== undefined && first <= period.deadline;
}

/** Versão de um resultado (célula = KPI + período + unidade), para a métrica de planilhas. */
export type ResultVersion = {
  cell: string;
  version: number;
  value: number | null;
  source: string;
  createdAt: Date;
};
export type EntryCounts = {
  direct: number;
  imported: number;
  previousDirect: number;
  previousImported: number;
};

/**
 * Lançamentos de valor para a redução de planilhas (DEC-071): versões que lançam ou mudam o valor da célula — não as
 * que só anexam ou trocam evidência (mesmo valor) nem as remoções (sem valor) —, digitadas no sistema (MANUAL) ou
 * importadas de planilha (IMPORTED), na janela atual (a partir de `currentStart`) ou na anterior (de
 * `previousStart` até ela). Recebe todas as versões das células (para comparar com a versão anterior).
 */
export function countValueEntries(
  versions: ResultVersion[],
  currentStart: Date,
  previousStart: Date,
): EntryCounts {
  const out: EntryCounts = { direct: 0, imported: 0, previousDirect: 0, previousImported: 0 };
  const byCell = new Map<string, ResultVersion[]>();
  for (const v of versions) byCell.set(v.cell, [...(byCell.get(v.cell) ?? []), v]);
  for (const list of byCell.values()) {
    let previous: number | null = null;
    for (const v of [...list].sort((a, b) => a.version - b.version)) {
      const launches = v.value !== null && v.value !== previous;
      previous = v.value;
      if (!launches || v.createdAt < previousStart) continue;
      if (v.source !== 'MANUAL' && v.source !== 'IMPORTED') continue;
      const current = v.createdAt >= currentStart;
      if (v.source === 'IMPORTED') {
        if (current) out.imported += 1;
        else out.previousImported += 1;
      } else if (current) out.direct += 1;
      else out.previousDirect += 1;
    }
  }
  return out;
}

/** Ação concluída no prazo: concluída até o dia do prazo (data local da conclusão). */
export const actionOnTime = (a: { status: string; dueDate: string; completedOn: string | null }) =>
  a.status === 'DONE' && a.completedOn !== null && a.completedOn <= a.dueDate;

/** Dias (com fração) entre o desvio e o primeiro plano criado a partir dele. */
export const daysBetween = (from: Date, to: Date) => (to.getTime() - from.getTime()) / DAY_MS;

/** Média com uma casa (null sem amostras). */
export const average = (values: number[]) =>
  values.length ? Math.round((values.reduce((s, v) => s + v, 0) / values.length) * 10) / 10 : null;

/** Dias com acesso por semana: total de dias com acesso ÷ pessoas ÷ semanas da janela (uma casa). */
export const accessDaysPerWeek = (accessDays: number, people: number, windowDays: number) =>
  people === 0 ? null : Math.round((accessDays / people / (windowDays / 7)) * 10) / 10;

const ratio = z.object({ total: z.number().int(), hit: z.number().int(), rate: z.number().nullable() });

export const successMetricsSchema = z.object({
  /** % KPIs atualizados no prazo: períodos com prazo na janela e quantos foram lançados até o prazo. */
  kpisOnTime: ratio,
  /** % ações concluídas no prazo: ações com prazo na janela e quantas foram concluídas até o prazo. */
  actionsOnTime: ratio,
  /** Desvio → ação: desvios abertos na janela, quantos ganharam plano e a média de dias até o plano. */
  deviationToAction: z.object({
    deviations: z.number().int(),
    withAction: z.number().int(),
    averageDays: z.number().nullable(),
  }),
  /** Usuários: contas ativas e quem acessou na janela. */
  users: z.object({ active: z.number().int(), accessed: z.number().int() }),
  /** Executivos (perfil Diretor / CEO): ativos, quantos acessaram e a média de dias com acesso por semana. */
  executives: z.object({
    active: z.number().int(),
    accessed: z.number().int(),
    accessDaysPerWeek: z.number().nullable(),
  }),
  /** Projetos em andamento e quantos registraram análise crítica na janela. */
  criticalAnalysis: ratio,
  /**
   * Redução de controles paralelos em planilha: resultados lançados na janela direto no sistema e importados de
   * planilha, e a parte lançada direto na janela e na janela anterior (de mesmo tamanho).
   */
  spreadsheet: z.object({
    direct: z.number().int(),
    imported: z.number().int(),
    rate: z.number().nullable(),
    previousRate: z.number().nullable(),
  }),
});
export type SuccessMetrics = z.infer<typeof successMetricsSchema>;

export const successReportSchema = z.object({
  window: z.object({ days: z.number().int(), from: z.string(), to: z.string() }),
  consolidated: successMetricsSchema.extend({
    /** Clientes ativos e quantos usaram a plataforma na janela. */
    clients: z.object({ active: z.number().int(), using: z.number().int() }),
    /** Média de usuários que acessaram por cliente ativo. */
    activeUsersPerClient: z.number().nullable(),
  }),
  companies: z.array(
    z.object({
      organization: z.object({ id: z.string().uuid(), name: z.string() }),
      /** Alguém da empresa acessou na janela. */
      using: z.boolean(),
      metrics: successMetricsSchema,
    }),
  ),
});
export type SuccessReport = z.infer<typeof successReportSchema>;
